import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { PROGRAM_MAPPING } from '../utils/dataProcessor';
import MultiSelectFilter from './MultiSelectFilter';
import { CentersModal } from './CentersModal';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  Target,
  Filter,
  X,
  RefreshCw,
  BookOpen,
  ChevronDown,
  Lightbulb,
  SlidersHorizontal,
  BarChart3,
  Globe,
  Map,
  Briefcase,
  Settings,
  FileText,
} from 'lucide-react';

const reportSections = [
  {
    id: 'overview', label: '1. Overview', icon: LayoutDashboard,
    subsections: {
      program: [
        { id: 'overview-figure', label: 'Figures & Visualizations' },
        { id: 'overview-factsheet', label: 'Factsheet' },
        { id: 'overview-about', label: 'About the Program' },
        { id: 'overview-executive-summary', label: 'Executive Summary' },
      ],
      accelerator: [
        { id: 'overview-figure', label: 'Figures & Visualizations' },
        { id: 'overview-factsheet', label: 'Factsheet' },
        { id: 'overview-about', label: 'About the Accelerator' },
        { id: 'overview-executive-summary', label: 'Executive Summary' },
      ],
    },
  },
  {
    id: 'progress', label: '2. Annual Progress', icon: TrendingUp,
    subsections: [
      { id: 'progress-toc', label: 'Progress along ToC' },
      { id: 'progress-figures', label: 'Figures & Visualizations' },
      { id: 'progress-framework', label: 'ToC Framework' },
      { id: 'progress-aow', label: 'Area of Work Progress' },
      { id: 'progress-nontoc', label: 'Non-TOC Results' },
    ],
  },
  {
    id: 'partnerships', icon: Users,
    label: { program: '3. Partnerships', accelerator: '3. Portfolio Linkages' },
    subsections: [
      { id: 'partnerships-narrative', label: 'Narrative' },
      { id: 'partnerships-charts', label: 'Figures & Visualizations' },
      { id: 'partnerships-browser', label: 'Partner Browser' },
    ],
  },
  {
    id: 'adaptive', label: '4. Adaptive Management', icon: RefreshCw, disabled: true,
    subsections: [
      { id: 'adaptive-evidence', label: 'Evidence-Informed Decision-Making' },
      { id: 'adaptive-resources', label: 'Flexible Resource Allocation' },
      { id: 'adaptive-bilaterals', label: 'Bilaterals/W3 Mapping' },
      { id: 'adaptive-risk', label: 'Risk Management' },
      { id: 'adaptive-scaling', label: 'Scaling for Impact' },
      { id: 'adaptive-prioritization', label: 'Prioritization & Comparative Advantage' },
    ],
  },
  {
    id: 'keyresults', label: '5. Key Result Story', icon: BookOpen, disabled: true,
    subsections: 'dynamic',
  },
  {
    id: 'innovation', label: 'Innovation Analytics', icon: Lightbulb,
    subsections: [
      { id: 'innovation-overview', label: 'Overview' },
      // Recommended tabs
      { id: 'innovation-types', label: 'Results by Type', tab: 'types' },
      { id: 'innovation-pipeline', label: 'Pipeline', tab: 'pipeline' },
      { id: 'innovation-geography', label: 'Geography', tab: 'geography' },
      { id: 'innovation-crossprogram', label: 'Cross-Program', tab: 'crossProgram' },
      // Additional tabs
      { id: 'innovation-nature', label: 'Nature & Typology', tab: 'nature' },
      { id: 'innovation-evolution', label: 'Evolution', tab: 'evolution' },
      { id: 'innovation-linkages', label: 'Linkages', tab: 'linkages' },
      { id: 'innovation-packages', label: 'Packages', tab: 'packages' },
      { id: 'innovation-users', label: 'Users & Reach', tab: 'users' },
      { id: 'innovation-discontinued', label: 'Discontinued', tab: 'discontinued' },
      { id: 'innovation-impact', label: 'Impact Areas', tab: 'impact' },
      { id: 'innovation-partners', label: 'Partners', tab: 'partners' },
      { id: 'innovation-collaboration', label: 'Collaboration', tab: 'collaboration' },
    ],
  },
  {
    id: 'highlights', label: 'Indicator Highlights', icon: BarChart3,
    subsections: [
      { id: 'highlights-overview', label: 'Overview' },
    ],
  },
  {
    id: 'custom', label: 'Custom Analysis', icon: SlidersHorizontal,
    subsections: 'dynamic',
  },
];

const portfolioSections = [
  {
    id: 'portfolio-overview', label: '1. Portfolio Overview', icon: LayoutDashboard,
    subsections: [
      { id: 'pf-overview-figures', label: 'Figures & Visualizations' },
    ],
  },
  {
    id: 'portfolio-impact', label: '2. Impact Area Focus', icon: Target,
    subsections: [
      { id: 'pf-impact-gender', label: 'Gender Equality', tab: 'gender' },
      { id: 'pf-impact-climate', label: 'Climate Adaptation', tab: 'climate' },
      { id: 'pf-impact-nutrition', label: 'Nutrition & Health', tab: 'nutrition' },
      { id: 'pf-impact-biodiversity', label: 'Biodiversity', tab: 'biodiversity' },
      { id: 'pf-impact-poverty', label: 'Poverty Reduction', tab: 'poverty' },
    ],
  },
  {
    id: 'portfolio-regional', label: '3. Regional Focus', icon: Globe,
    subsections: [
      { id: 'pf-regional-africa', label: 'Africa', tab: 'africa' },
      { id: 'pf-regional-americas', label: 'The Americas', tab: 'americas' },
      { id: 'pf-regional-asia', label: 'Asia', tab: 'asia' },
    ],
  },
  {
    id: 'portfolio-partnerships', label: '4. Partnerships', icon: Users,
    subsections: [
      { id: 'portfolio-partnerships-figure', label: 'Figures & Visualizations' },
      { id: 'portfolio-partnerships-narrative', label: 'Narrative' },
    ],
  },
  {
    id: 'portfolio-performance', label: '5. Performance Management', icon: Settings, disabled: true,
    subsections: [
      { id: 'perf-perfProjectedBenefits', label: 'Projected Benefits' },
      { id: 'perf-perfInnovationManagement', label: 'Innovation Management' },
      { id: 'perf-perfAdaptiveManagement', label: 'Adaptive Management' },
      { id: 'perf-perfMraTracking', label: 'MRA Tracking' },
    ],
  },
  {
    id: 'custom', label: 'Custom Analysis', icon: SlidersHorizontal,
    subsections: 'dynamic',
  },
];


export default function Sidebar({ isOpen, onClose }) {
  const { activeSection, setActiveSection, setSectionTab, stats, reportType, setReportType, reportMode, setReportMode, filterOptions, filters, setFilters, resetFilters, sections, filteredResults } = useApp();
  const [showCentersModal, setShowCentersModal] = useState(false);

  // Select which section list to show based on report mode
  const activeSections = reportMode === 'portfolio' ? portfolioSections : reportSections;

  // Get display name for a program/initiative code
  const getProgramDisplayName = (code) => {
    const mapped = PROGRAM_MAPPING[code];
    if (mapped) return mapped;
    // For codes not in mapping, just return the code
    return code;
  };

  // Classify whether a code is an Accelerator (SP10–SP13)
  const isAcceleratorCode = (code) => {
    if (!code || code === 'all') return false;
    const spCode = code.startsWith('SP') ? code : PROGRAM_MAPPING[code];
    if (!spCode?.startsWith('SP')) return false;
    const num = parseInt(spCode.replace('SP', ''), 10);
    return num >= 10 && num <= 13;
  };

  // Handle program filter change — also auto-sets report type
  const handleProgramChange = (value) => {
    setFilters({ program: value });
    setReportType(isAcceleratorCode(value) ? 'accelerator' : 'program');
  };

  // Get current filter label for display
  const getCurrentFilterLabel = () => {
    if (!filters.program || filters.program === 'all') {
      return 'All Programs/Initiatives';
    }
    return getProgramDisplayName(filters.program);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-[var(--surface-primary)] border-r border-[var(--border-primary)] z-50 transform transition-transform duration-300 lg:sticky lg:top-0 lg:h-[calc(100vh-60px)] lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--border-primary)] lg:hidden">
            <span className="font-semibold text-[var(--text-primary)]">Menu</span>
            <button onClick={onClose} className="p-2 hover:bg-[var(--surface-hover)] rounded-lg">
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>

          {/* Scrollable content — filters, stats, nav, footer */}
          <div className="flex-1 overflow-y-auto min-h-0">
          {/* Report mode switcher — Technical vs Portfolio Narrative */}
          <div className="px-4 pt-4 pb-3 border-b border-[var(--border-primary)]">
            <label className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide mb-2 block">Report Mode</label>
            <div className="flex rounded-lg border border-[var(--input-border)] overflow-hidden text-xs">
              {[
                { value: 'technical', label: 'Annual Report', icon: FileText },
                { value: 'portfolio', label: 'Portfolio Narrative', icon: Globe },
              ].map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setReportMode(opt.value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 transition-colors ${
                      reportMode === opt.value
                        ? 'bg-cgiar-green text-white font-medium'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {reportMode === 'technical' && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-tertiary)]">Type:</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-cgiar-green/10 text-cgiar-green">
                  {reportType === 'accelerator' ? 'Accelerator' : 'Program'}
                </span>
              </div>
            )}
          </div>

          {/* Global Program/Accelerator Filter — hidden in Portfolio mode */}
          {reportMode === 'technical' && filterOptions?.programs?.length > 0 && (
            <div className="p-4 border-b border-[var(--border-primary)] bg-amber-50/50">
              <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-secondary)] mb-2">
                <Filter className="w-3.5 h-3.5" />
                Filter by Program / Accelerator
              </label>
              <div className="relative">
                <select
                  value={filters.program || 'all'}
                  onChange={(e) => handleProgramChange(e.target.value)}
                  className="w-full px-3 py-2 pr-8 text-sm border border-[var(--input-border)] rounded-lg bg-[var(--input-bg)] text-[var(--text-primary)] focus:ring-2 focus:ring-cgiar-green focus:border-transparent appearance-none cursor-pointer"
                >
                  <option value="all">All Programs / Accelerators</option>
                  {filterOptions.programs
                    .filter(p => {
                      if (!p.startsWith('SP')) return false;
                      const num = parseInt(p.replace('SP', ''), 10);
                      return num >= 1 && num <= 9;
                    }).length > 0 && (
                    <optgroup label="Programs">
                      {filterOptions.programs
                        .filter(p => {
                          if (!p.startsWith('SP')) return false;
                          const num = parseInt(p.replace('SP', ''), 10);
                          return num >= 1 && num <= 9;
                        })
                        .map(code => (
                          <option key={code} value={code}>
                            {getProgramDisplayName(code)}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {filterOptions.programs
                    .filter(p => {
                      if (!p.startsWith('SP')) return false;
                      const num = parseInt(p.replace('SP', ''), 10);
                      return num >= 10 && num <= 13;
                    }).length > 0 && (
                    <optgroup label="Accelerators">
                      {filterOptions.programs
                        .filter(p => {
                          if (!p.startsWith('SP')) return false;
                          const num = parseInt(p.replace('SP', ''), 10);
                          return num >= 10 && num <= 13;
                        })
                        .map(code => (
                          <option key={code} value={code}>
                            {getProgramDisplayName(code)}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)] pointer-events-none" />
              </div>
              {filters.program && filters.program !== 'all' && (
                <>
                  <div className="mt-2 flex rounded-lg border border-[var(--input-border)] overflow-hidden text-xs">
                    {[
                      { value: 'all', label: 'All Results' },
                      { value: 'submitted', label: 'Submitted' },
                      { value: 'contributed', label: 'Contributions' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setFilters({ resultScope: opt.value })}
                        className={`flex-1 px-2 py-1.5 transition-colors ${
                          (filters.resultScope || 'all') === opt.value
                            ? 'bg-cgiar-green text-white font-medium'
                            : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handleProgramChange('all')}
                    className="mt-2 text-xs text-cgiar-green hover:underline flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear filter
                  </button>
                </>
              )}
            </div>
          )}

          {/* Global data filters (Status, Reporting Phase, Funding Type) */}
          {(filterOptions?.statuses?.length > 0 || filterOptions?.reportingPhases?.length > 0 || true /* funding type always available */) && (
            <div className="px-4 py-3 border-b border-[var(--border-primary)]">
              <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-tertiary)] mb-2 uppercase tracking-wide">
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Data Filters
              </label>

              <div className="space-y-2">
                {/* Status filter (multi-select) */}
                {filterOptions?.statuses?.length > 0 && (
                  <div>
                    <label className="text-[11px] text-[var(--text-tertiary)] mb-0.5 block">Status</label>
                    <MultiSelectFilter
                      size="sm"
                      options={filterOptions.statuses}
                      selected={filters.status}
                      onChange={(val) => setFilters({ status: val })}
                      allLabel="All statuses"
                    />
                  </div>
                )}

                {/* Reporting Phase filter (multi-select) */}
                {filterOptions?.reportingPhases?.length > 0 && (
                  <div>
                    <label className="text-[11px] text-[var(--text-tertiary)] mb-0.5 block">Reporting Phase</label>
                    <MultiSelectFilter
                      size="sm"
                      options={filterOptions.reportingPhases}
                      selected={filters.reportingPhase}
                      onChange={(val) => setFilters({ reportingPhase: val })}
                      allLabel="All phases"
                    />
                  </div>
                )}

                {/* Funding Type filter (multi-select) */}
                <div>
                  <label className="text-[11px] text-[var(--text-tertiary)] mb-0.5 block">Funding Type</label>
                  <MultiSelectFilter
                    size="sm"
                    options={[
                      { value: 'W1W2', label: 'W1&W2 (Pooled)' },
                      { value: 'W3Bilateral', label: 'W3/Bilateral' },
                    ]}
                    selected={filters.fundingType}
                    onChange={(val) => setFilters({ fundingType: val })}
                    allLabel="All funding types"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-[var(--text-tertiary)] mb-0.5 block">Geographic Focus</label>
                  <select
                    value={filters.geographicFocus || 'all'}
                    onChange={(e) => setFilters({ geographicFocus: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--input-border)] rounded-lg bg-[var(--input-bg)] text-[var(--text-primary)] focus:ring-2 focus:ring-cgiar-green focus:border-transparent"
                  >
                    <option value="all">All countries</option>
                    <option value="globalSouth">Global South</option>
                  </select>
                </div>
              </div>

              {/* Reset data filters to defaults */}
              <button
                onClick={resetFilters}
                className="mt-2 text-xs text-cgiar-green hover:underline flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Reset to default
              </button>
            </div>
          )}

          {/* Stats summary */}
          {stats && (
            <div className="p-4 bg-[var(--surface-secondary)] border-b border-[var(--border-primary)]">
              <h3 className="text-xs font-medium text-[var(--text-tertiary)] mb-2">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[var(--surface-primary)] rounded-lg p-2 border border-[var(--border-primary)]">
                  <p className="text-lg font-bold text-cgiar-green">{stats.total}</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Total Results</p>
                </div>
                <div className="bg-[var(--surface-primary)] rounded-lg p-2 border border-[var(--border-primary)]">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-bold text-cgiar-green">{stats.byFundingType?.pooled || 0}</p>
                    <span className="text-xs text-[var(--text-tertiary)]">/</span>
                    <p className="text-sm font-bold text-orange-500">{stats.byFundingType?.nonpooled || 0}</p>
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)]">W1W2 / W3</p>
                </div>
                <div className="bg-[var(--surface-primary)] rounded-lg p-2 border border-[var(--border-primary)]">
                  <p className="text-lg font-bold text-cgiar-orange">{stats.countries?.length || 0}</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Countries</p>
                </div>
                <div className="bg-[var(--surface-primary)] rounded-lg p-2 border border-[var(--border-primary)] cursor-pointer hover:border-teal-400 transition-colors"
                     onClick={() => setShowCentersModal(true)}>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-teal-500">{stats.contributingCenters?.length || 0}</p>
                    <BarChart3 className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)]">Centers</p>
                </div>
                <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wide col-span-2 mt-1 mb-0">Partnerships</p>
                <div className="bg-[var(--surface-primary)] rounded-lg p-2 border border-[var(--border-primary)] col-span-2">
                  <p className="text-sm font-bold text-[#8B5CF6]">{stats.externalPartners?.length || 0}</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Partnerships</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="p-4">
            <p className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide px-3 mb-2">
              {reportMode === 'portfolio' ? 'Portfolio Narrative' : 'Report Sections'}
            </p>
            <ul className="space-y-1">
              {activeSections.map(section => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                const sectionLabel = typeof section.label === 'object'
                  ? section.label[reportType] || section.label.program
                  : section.label;
                let subs;
                if (section.subsections === 'dynamic' && section.id === 'keyresults') {
                  const storiesArr = sections?.keyResultsStories?.length
                    ? sections.keyResultsStories
                    : [{}];
                  subs = [
                    { id: 'keyresults-candidates', label: 'KRS Candidates' },
                    ...storiesArr.map((_, i) => ({ id: `keyresults-story-${i + 1}`, label: `Story ${i + 1}` })),
                  ];
                } else if (section.subsections === 'dynamic' && section.id === 'custom') {
                  const blocks = sections?.customBlocks || [];
                  subs = blocks.map((b, i) => ({
                    id: `custom-block-${i}`,
                    label: b.label || `Block ${i + 1}`,
                  }));
                } else if (Array.isArray(section.subsections)) {
                  subs = section.subsections;
                } else {
                  subs = section.subsections?.[reportType] || section.subsections?.program || [];
                }

                if (section.hidden) return null;

                if (section.disabled) {
                  return (
                    <li key={section.id}>
                      <div
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[var(--text-tertiary)] cursor-not-allowed opacity-60"
                        title="Manual only — no AI assistance"
                      >
                        <Icon className="w-5 h-5" />
                        <span className="flex-1 text-left">{sectionLabel}</span>
                        <span className="text-[10px] uppercase tracking-wide bg-[var(--surface-tertiary)] text-[var(--text-tertiary)] px-1.5 py-0.5 rounded">Manual</span>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-cgiar-green text-white'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{sectionLabel}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isActive ? 'rotate-0' : '-rotate-90'
                        } ${isActive ? 'text-white/70' : 'text-[var(--text-tertiary)]'}`}
                      />
                    </button>
                    {isActive && subs.length > 0 && (
                      <ul className="ml-8 pl-3 border-l-2 border-cgiar-green/20 mt-1 space-y-0.5">
                        {subs.map(sub => (
                          <li key={sub.id}>
                            <button
                              onClick={() => {
                                // For tab-based sub-sections (e.g. Innovation), switch the active tab
                                if (sub.tab) {
                                  setSectionTab(section.id, sub.tab);
                                }
                                // Scroll to the target element (for tab items, scroll to the section top)
                                const targetId = sub.tab ? `${section.id}-overview` : sub.id;
                                const el = document.getElementById(targetId);
                                if (el) {
                                  el.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                  // Section may not be rendered yet — wait for React to paint
                                  requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
                                    });
                                  });
                                }
                                // On mobile, close sidebar after clicking subsection
                                if (window.innerWidth < 1024) onClose();
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs text-[var(--text-secondary)] hover:text-cgiar-green hover:bg-cgiar-green/5 rounded transition-colors"
                            >
                              {sub.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-[var(--border-primary)]">
            <div className="text-xs text-[var(--text-tertiary)] text-center">
              <p>CGIAR Portfolio Performance Team</p>
              <p>Office of the Chief Scientist</p>
            </div>
          </div>
          </div>
        </div>
      </aside>
      <CentersModal isOpen={showCentersModal} onClose={() => setShowCentersModal(false)} filteredResults={filteredResults} />
    </>
  );
}
